import { deleteProductThunk, getProductsThunk, exportProductsThunk } from '@redux/features/product.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import React, { FC, useEffect, useState, useCallback } from 'react';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import UpdateProductModal from './UpdateProductModal';
import AddProductModal from './AddProductModal';
import ProductLifeCycleModal from './ProductLifeCycleModal';
import InventoryProductHistoryModal from './InventoryProductHistoryModal';
import Pagination from 'Common/Pagination';
import MultiSelectFilter from 'Shared/MultiSelectFilter';
import FilterInput from 'Shared/FilterInput';
import DebouncedInput from 'Shared/DebouncedInput';
import ExportButton from 'Shared/ExportButton';
import UploadImageThumb from 'components/ui/UploadImageThumb';
import { RequestStatus } from '@core/enums/request-status.enum';
import { toast } from 'react-toastify';

const Products: FC = () => {
    // -----------------------------------------------------------------
    const [page, setPage] = useState<number>(1);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [showLifeCycleModal, setShowLifeCycleModal] = useState<boolean>(false);
    const [showInventoryHistoryModal, setShowInventoryHistoryModal] = useState<boolean>(false);
    const [productId, setProductId] = useState<number>();
    const [selectedProductId, setSelectedProductId] = useState<number>();
    // Filters
    const [filterSubCategory, setFilterSubCategory] = useState<string[]>([]);
    const [filterDosage, setFilterDosage] = useState<string[]>([]);
    const [filterCategory, setFilterCategory] = useState<string[]>([]);
    const [filterInStock, setFilterInStock] = useState<string>('');
    const [filterProductName, setFilterProductName] = useState<string>('');
    // -----------------------------------------------------------------

    const dispatch = useAppDispatch();
    const { products, loading } = useAppSelector((state: RootState) => state.productState);
    // -----------------------------------------------------------------

    const changePage = (p: string) => setPage(Number(p));
    // -----------------------------------------------------------------

    // Memoized search function to prevent pagination issues
    const handleProductNameSearch = useCallback(
        (v: string) => {
            if (v !== filterProductName) {
                setFilterProductName(v);
                setPage(1);
            }
        },
        [filterProductName]
    );

    // Convert filterInStock string to boolean or undefined
    const getInStockBoolean = (): boolean | undefined => {
        if (filterInStock === '') return undefined;
        return filterInStock === 'true';
    };

    useEffect(() => {
        dispatch(
            getProductsThunk({
                page: page - 1,
                productSize: 10,
                subCategory: filterSubCategory.length > 0 ? filterSubCategory.join(',') : undefined,
                dosage: filterDosage.length > 0 ? filterDosage.join(',') : undefined,
                category: filterCategory.length > 0 ? filterCategory.join(',') : undefined,
                inStock: getInStockBoolean(),
                productName: filterProductName.trim() !== '' ? filterProductName.trim() : undefined,
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filterSubCategory, filterDosage, filterCategory, filterInStock, filterProductName]);
    // -----------------------------------------------------------------

    const handleConfirmDelete = async () => {
        if (productId) {
            dispatch(deleteProductThunk(productId)).then((response: any) => {
                if (response.meta.requestStatus === 'fulfilled') {
                    setShowDeleteModal(false);
                    setProductId(undefined);
                    const pageSize = (products as any)?.pageable?.pageSize || 100;
                    dispatch(
                        getProductsThunk({
                            page: page - 1,
                            productSize: pageSize,
                            subCategory: filterSubCategory.length > 0 ? filterSubCategory.join(',') : undefined,
                            dosage: filterDosage.length > 0 ? filterDosage.join(',') : undefined,
                            category: filterCategory.length > 0 ? filterCategory.join(',') : undefined,
                            inStock: getInStockBoolean(),
                            productName: filterProductName.trim() !== '' ? filterProductName.trim() : undefined,
                        })
                    );
                }
            });
        }
    };

    const handleDelete = (id: number) => {
        setProductId(id);
        setShowDeleteModal(true);
    };

    const handleUpdate = (id: number) => {
        setProductId(id);
        setShowUpdateModal(true);
    };

    const handleOpenLifeCycleModal = (id: number) => {
        setSelectedProductId(id);
        setShowLifeCycleModal(true);
    };

    const handleOpenInventoryHistoryModal = (id: number) => {
        setSelectedProductId(id);
        setShowInventoryHistoryModal(true);
    };

    const handleExport = () => {
        const subCategories = filterSubCategory.length > 0 ? filterSubCategory.join(',') : undefined;
        const category = filterCategory.length > 0 ? filterCategory.join(',') : undefined;
        const dosages = filterDosage.length > 0 ? filterDosage.join(',') : undefined;
        const inStockOnly = getInStockBoolean();

        dispatch(exportProductsThunk({ subCategories, category, dosages, inStockOnly })).then((res: any) => {
            if (res.meta.requestStatus === RequestStatus.FULFILLED) {
                const downloadUrl = res.payload?.exportRequest?.downloadUrl || res.payload?.exportRequest?.filePath;
                if (downloadUrl) {
                    const a = document.createElement('a');
                    a.href = downloadUrl;
                    a.download = 'Products.csv';
                    a.target = '_blank';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } else {
                    toast.error('Download URL not found in response', { autoClose: 2000 });
                }
            }
        });
    };
    // -----------------------------------------------------------------

    return (
        <>
            <UpdateProductModal
                show={showUpdateModal}
                handleClose={() => setShowUpdateModal(false)}
                productId={productId || 0}
            />
            <AddProductModal show={showAddModal} handleClose={() => setShowAddModal(false)} />
            <ProductLifeCycleModal
                show={showLifeCycleModal}
                handleClose={() => {
                    setShowLifeCycleModal(false);
                    setSelectedProductId(undefined);
                }}
                productId={selectedProductId || 0}
            />
            <InventoryProductHistoryModal
                show={showInventoryHistoryModal}
                handleClose={() => {
                    setShowInventoryHistoryModal(false);
                    setSelectedProductId(undefined);
                }}
                productId={selectedProductId || 0}
            />
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => {
                    setShowDeleteModal(false);
                    setProductId(undefined);
                }}
                onConfirm={handleConfirmDelete}
                title="Delete Product"
                message="Are you sure you want to delete this product? This action cannot be undone."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                id={productId}
            />
            <BreadCrumb pageTitle={`Products List`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Products List</h3>
                        <ButtonBase innerText="Add Product" onClick={() => setShowAddModal(true)} />
                    </div>
                    {/* Filters Row */}
                    <div className="flex flex-wrap gap-4">
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Sub Category</label>
                            <MultiSelectFilter
                                value={filterSubCategory}
                                onChange={setFilterSubCategory}
                                options={[
                                    { value: 'Cold Cough and Allergy', label: 'Cold, Cough, and Allergy' },
                                    { value: 'Digestive & Gastrointestinal', label: 'Digestive & Gastrointestinal' },
                                    { value: 'Dermatologicals', label: 'Dermatologicals' },
                                    {
                                        value: 'Vitamins & Nutritional Supplements',
                                        label: 'Vitamins & Nutritional Supplements',
                                    },
                                    { value: 'Eye Ear Nose and Throat', label: 'Eye, Ear, Nose, and Throat' },
                                    { value: 'Sleep Aids & Sedatives', label: 'Sleep Aids & Sedatives' },
                                    { value: 'Smoking Cessation', label: 'Smoking Cessation' },
                                    { value: 'Sickness & Nausea Relief', label: 'Sickness & Nausea Relief' },
                                    {
                                        value: 'Feminine Health & Contraceptives',
                                        label: 'Feminine Health & Contraceptives',
                                    },
                                    {
                                        value: "Infant & Children's Medications",
                                        label: "Infant & Children's Medications",
                                    },
                                    { value: 'Antiseptics & Disinfectants', label: 'Antiseptics & Disinfectants' },
                                    { value: 'Oral Care', label: 'Oral Care' },
                                    { value: 'Weight Management', label: 'Weight Management' },
                                    { value: 'Analgesics & Antipyretics', label: 'Analgesics & Antipyretics' },
                                    { value: 'Cardiovascular Drugs', label: 'Cardiovascular Drugs' },
                                    {
                                        value: 'Central Nervous System (CNS) Agents',
                                        label: 'Central Nervous System (CNS) Agents',
                                    },
                                    { value: 'Endocrine & Metabolic Drugs', label: 'Endocrine & Metabolic Drugs' },
                                    { value: 'Gastrointestinal Agents', label: 'Gastrointestinal Agents' },
                                    { value: 'Anti-Infective Agents', label: 'Anti-Infective Agents' },
                                    { value: 'Respiratory Drugs', label: 'Respiratory Drugs' },
                                    {
                                        value: 'Antineoplastic (Cancer) Agents',
                                        label: 'Antineoplastic (Cancer) Agents',
                                    },
                                    { value: 'Hematologic Agents', label: 'Hematologic Agents' },
                                    { value: 'Musculoskeletal Drugs', label: 'Musculoskeletal Drugs' },
                                    { value: 'Genitourinary Drugs', label: 'Genitourinary Drugs' },
                                    { value: 'Dermatologic Agents', label: 'Dermatologic Agents' },
                                    { value: 'Ophthalmic Agents', label: 'Ophthalmic Agents' },
                                    { value: 'Otic Agents', label: 'Otic Agents' },
                                    { value: 'Immunologic Agents', label: 'Immunologic Agents' },
                                    { value: 'Psychiatric Medications', label: 'Psychiatric Medications' },
                                    { value: 'Antiviral Agents', label: 'Antiviral Agents' },
                                    { value: 'Anesthetics', label: 'Anesthetics' },
                                    { value: 'Vaccines', label: 'Vaccines' },
                                    { value: 'Biologics & Biosimilars', label: 'Biologics & Biosimilars' },
                                    { value: 'Skin Care', label: 'Skin Care' },
                                    { value: 'Hair Care', label: 'Hair Care' },
                                    { value: 'Bath & Body', label: 'Bath & Body' },
                                    { value: 'Deodorants & Antiperspirant', label: 'Deodorants & Antiperspirant' },
                                    { value: "Men's Grooming", label: "Men's Grooming" },
                                    { value: "Women's Grooming", label: "Women's Grooming" },
                                    { value: 'Shaving & Hair Removal', label: 'Shaving & Hair Removal' },
                                    { value: 'Lip Care', label: 'Lip Care' },
                                    { value: 'Hand & Foot Care', label: 'Hand & Foot Care' },
                                    { value: 'Sun Care & After Sun', label: 'Sun Care & After Sun' },
                                    { value: 'Baby Care', label: 'Baby Care' },
                                    { value: 'Incontinence & Adult Care', label: 'Incontinence & Adult Care' },
                                    { value: 'Fragrances & Body Sprays', label: 'Fragrances & Body Sprays' },
                                    { value: 'Feminine Hygiene', label: 'Feminine Hygiene' },
                                ]}
                                id="subCategory"
                                placeholder="Select sub categories"
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Dosage</label>
                            <MultiSelectFilter
                                value={filterDosage}
                                onChange={setFilterDosage}
                                options={[
                                    { value: 'Tablet', label: 'Tablet' },
                                    { value: 'Capsule', label: 'Capsule' },
                                    { value: 'Powder', label: 'Powder' },
                                    { value: 'Lozenges', label: 'Lozenges' },
                                    { value: 'Syrup', label: 'Syrup' },
                                    { value: 'Suspension', label: 'Suspension' },
                                    { value: 'Ointment', label: 'Ointment' },
                                    { value: 'Cream', label: 'Cream' },
                                    { value: 'Gel', label: 'Gel' },
                                    { value: 'Paste', label: 'Paste' },
                                    { value: 'Lotion', label: 'Lotion' },
                                    { value: 'Solution', label: 'Solution' },
                                    { value: 'Spray', label: 'Spray' },
                                    { value: 'Vial', label: 'Vial' },
                                    { value: 'Syringe', label: 'Syringe' },
                                    { value: 'Eye Drops', label: 'Eye Drops' },
                                ]}
                                id="dosage"
                                placeholder="Select dosages"
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Category</label>
                            <MultiSelectFilter
                                value={filterCategory}
                                onChange={setFilterCategory}
                                options={[
                                    { value: 'Personal Care', label: 'Personal Care' },
                                    { value: 'OTC', label: 'OTC' },
                                    { value: 'RX', label: 'RX' },
                                ]}
                                id="category"
                                placeholder="Select categories"
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">In Stock</label>
                            <FilterInput
                                value={filterInStock}
                                onChange={(v) => setFilterInStock(v)}
                                options={[
                                    { value: '', label: 'All' },
                                    { value: 'true', label: 'In Stock' },
                                    { value: 'false', label: 'Out of Stock' },
                                ]}
                                id="inStock"
                                placeholder="All"
                                optionLabelKey="label"
                                optionValueKey="value"
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                                Product Name And NDC
                            </label>
                            <DebouncedInput
                                placeholder="Search product name And NDC"
                                searchValue={filterProductName}
                                searchFn={handleProductNameSearch}
                            />
                        </div>
                        <div className="flex-1 min-w-[100px] flex items-end">
                            <ExportButton onExport={handleExport} />
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Product Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Price Offer</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Unit Price</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Product Photo</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Quantity</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Sub Category</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Category Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">barcode</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">brand Generic</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    location In Warehouse
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">packSize</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">packaging Uom</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">strength</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">wholesale Uom</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    otc Prescription
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">gtin</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase text-center">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={products?.content?.length ?? 0} animationKey={`products-${page}`}>
                                            {products?.content?.map((product: any) => (
                                                <AnimatedTableRow
                                                    rowKey={product?.id}
                                                    detailRecord={product}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{product?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.itemName || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.priceOffer ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.unitPrice ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <UploadImageThumb
                                                            path={product?.productPhoto?.photoPath}
                                                            alt="product photo"
                                                        />
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{product?.initialQuantity ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {product?.subCategories?.categoryName || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {product?.category?.categoryName || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{product?.barcode ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.brandGeneric ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {product?.locationInWarehouse ?? `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{product?.packSize ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.packagingUom ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.strength ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.wholesaleUom ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.otcPrescription ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">{product?.gtin ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-col justify-center items-center gap-2 w-full">
                                                            <div className="flex flex-row justify-center items-center gap-2 w-full">
                                                                <button
                                                                    className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-blue-600 text-blue-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-blue-50 hover:border-blue-700 hover:scale-105 active:scale-95"
                                                                    onClick={() => {
                                                                        handleOpenLifeCycleModal(product?.id);
                                                                    }}
                                                                >
                                                                    Life Cycle
                                                                </button>
                                                                <button
                                                                    className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-green-600 text-green-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-green-50 hover:border-green-700 hover:scale-105 active:scale-95"
                                                                    onClick={() => {
                                                                        handleOpenInventoryHistoryModal(product?.id);
                                                                    }}
                                                                >
                                                                    Inventory
                                                                </button>
                                                                <button
                                                                    className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-red-500 text-red-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-red-50 hover:border-red-600 hover:scale-105 active:scale-95"
                                                                    onClick={() => {
                                                                        handleDelete(product?.id);
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                                <button
                                                                    className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-yellow-600 text-yellow-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-yellow-50 hover:border-yellow-700 hover:scale-105 active:scale-95"
                                                                    onClick={() => {
                                                                        handleUpdate(product?.id);
                                                                    }}
                                                                >
                                                                    Update
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={(products as any)?.pageable?.pageSize || 10}
                                    total={(products as any)?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={
                                        (products as any)?.numberOfElements || (products as any)?.content?.length || 0
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Products;
